import { Button, Stack, Skeleton } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { IntegrationType } from '@/types/integrations'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCreateIntegration,
  useGetIntegrations,
} from '@/service-library/hooks/integrations'
import { useGetIntegrationTypes } from '@/service-library/hooks/integration-types'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import IntegrationTypeCard from './IntegrationTypeCard'
import { useRootOrganization } from '../organizations/RootOrganizationProvider'

type NewIntegrationDialogProps = {
  listQueryKey: QueryKey
  overlay: OverlayState
}

export default function NewIntegrationDialog({
  listQueryKey,
  overlay,
}: NewIntegrationDialogProps) {
  const { project } = useProjectContext()
  const { rootOrganization } = useRootOrganization()

  const { integrations } = useGetIntegrations({
    filters: {
      limit: '100',
      project_id: project.id,
      fields__only: 'id,name,is_enabled,integration_type_id',
    },
  })

  const { integrationTypes, isPending } = useGetIntegrationTypes({
    refetchOnWindowFocus: false,
    enabled: overlay.isOpen,
    filters: {
      limit: '1000',
    },
  })

  const { createIntegration } = useCreateIntegration({
    listQueryKey,
    onError: (error) => {
      console.error('Error creating integration:', error)
    },
  })

  async function onAddIntegration(integrationType: IntegrationType) {
    createIntegration({
      id: generateUuid(),
      name: integrationType.name,
      color: 'blue',
      external_id: null,
      image_url: null,
      is_enabled: true,
      org_id: rootOrganization.id,
      project_id: project.id,
      metadata: {},
      org_credential_id: null,
      integration_type_id: integrationType.id,
    })
    overlay.close()
  }

  return (
    <Dialog {...overlay} title="New Integration">
      <DialogContent>
        <Stack spacing={1}>
          {isPending && (
            <>
              <Skeleton width={200} />
              <Skeleton width={150} />
              <Skeleton width={275} />
            </>
          )}

          {!isPending &&
            integrationTypes?.map((integrationType) => {
              return (
                <IntegrationTypeCard
                  key={integrationType.id}
                  integrationType={integrationType}
                  added={integrations?.some(
                    (integration) =>
                      integration.integration_type_id === integrationType.id,
                  )}
                  onAdd={(typeToAdd) => {
                    onAddIntegration(typeToAdd)
                  }}
                />
              )
            })}
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={overlay.close}>
          Cancel
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
